/**
 * Numbers, written the way a student writes them.
 *
 * The arithmetic is exact (`@padcanvas/shared/stoichiometry`); this is only how
 * it is READ, and the two jobs are deliberately apart — a rounding decision
 * must never reach the calculation it is displaying.
 *
 * Three significant figures throughout, because that is what a data booklet
 * gives and what a student's own working will carry. A bench that printed
 * 9.435030380797826 mmol would be stating a precision the molar masses behind
 * it do not have.
 *
 * The UNIT is chosen for the size of the number rather than fixed, for the same
 * reason a person would: 0.002 mol is 2 mmol on paper, and 0.024 L is 24 mL.
 */
import type { Substance } from './substanceCatalog';

/**
 * Three significant figures, trailing zeros KEPT — "2.00 mmol", not "2 mmol".
 *
 * They are not noise: they say the figure is known to that precision, which is
 * the convention the student's own working uses and the thing they are marked
 * on. What is trimmed is the exponent notation `toPrecision` reaches for at
 * either end, because "0.00234" reads and "2.34e-3" does not, to someone who
 * has not met it yet.
 */
export function sigFigs(value: number, figures = 3): string {
  if (!Number.isFinite(value)) return '—';
  if (value === 0) return '0';
  const fixed = value.toPrecision(figures);
  if (!fixed.includes('e')) return fixed;
  // Outside roughly 1e-7, which a bench's amounts do not reach — but a
  // readable number beats a correct-looking one either way.
  return value.toFixed(Math.max(0, figures - 1 - Math.floor(Math.log10(Math.abs(value)))));
}

/**
 * A number as a person would say it, trailing zeros dropped — for a value that
 * was MEASURED OR CHOSEN rather than computed.
 *
 * The distinction is the whole convention: what went in reads as it was
 * counted ("3 chips", "10 mL", "1 M"), and what came out reads to the
 * precision the arithmetic supports ("9.44 mmol"). Writing the input to three
 * figures claims a measurement nobody made.
 */
export function plain(value: number): string {
  return String(Number(value.toPrecision(6)));
}

/** Moles, in millimoles where a bench's amounts actually land. */
export function formatMoles(mol: number): string {
  if (mol === 0) return '0';
  return Math.abs(mol) < 1 ? `${sigFigs(mol * 1000)} mmol` : `${sigFigs(mol)} mol`;
}

/** A mass. Grams unless it is too small to write in them. */
export function formatMass(grams: number): string {
  if (grams === 0) return '0 g';
  return Math.abs(grams) < 0.001 ? `${sigFigs(grams * 1000)} mg` : `${sigFigs(grams)} g`;
}

/** A gas volume. Litres once there is a litre of it. */
export function formatVolume(litres: number): string {
  return Math.abs(litres) < 1 ? `${sigFigs(litres * 1000)} mL` : `${sigFigs(litres)} L`;
}

/** A concentration, always in mol/L — the bench has no reason to say anything
 *  else, and "M" is what the label on the bottle says. */
export function formatMolarity(molPerL: number): string {
  // Stated, not computed: this is the strength someone selected off a ladder
  // of bench values, so it is written the way the label is.
  return `${plain(molPerL)} M`;
}

/**
 * What a counted solid weighs — "1.5 g" for three 0.5 g chips. The counterpart
 * of a solution's strength: "3 chips" alone is only half an amount, and the
 * mass is what a student would put on the balance. Written as measured, not to
 * three figures, because it is an input. Null for anything not counted in
 * pieces: a powder is already dosed in grams.
 */
export function countedMass(substance: Substance, qty: number): string | null {
  const each = substance.dose?.unit === 'piece' ? substance.dose.pieceMassG : undefined;
  return each ? `${plain(qty * each)} g` : null;
}

/** A molar mass, as it appears in the division that uses it. */
export function formatMolarMass(gPerMol: number): string {
  return `${sigFigs(gPerMol, 5)} g/mol`;
}
